import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Calendar, FileText, Image, MessageSquare, ExternalLink, CreditCard, 
  Settings, Clock, AlertCircle, DollarSign, UserCheck, 
  ArrowRight, Award, Zap, Building2
} from 'lucide-react';
import { adminVerify, adminLogout, getDashboardStats } from '../../services/api';
const AdminDashboardPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('ifm_admin_token');
        if (!token) {
          setIsLoggedIn(false);
          navigate('/admin');
          return;
        }
        await adminVerify();
        setIsLoggedIn(true);
        loadDashboardStats();
      } catch (e) {
        setIsLoggedIn(false);
        adminLogout();
        navigate('/admin');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const loadDashboardStats = async () => {
    try {
      setStatsLoading(true);
      setError('');
      const data = await getDashboardStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load dashboard stats:', err);
      const errorMessage = err?.data?.message || err?.message || 'Unknown error occurred';
      setError(`Failed to load dashboard statistics: ${errorMessage}. Please check the console for details.`);
    } finally {
      setStatsLoading(false);
    }
  };


  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return Number(num).toLocaleString('en-IN');
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0';
    return `₹${Number(amount).toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return null;
  }

  const mainStats = stats ? [
    { 
      title: 'Total Members', 
      value: formatNumber(stats.overview.totalMembers),
      subtitle: `${formatNumber(stats.overview.approvedMembers)} Approved`,
      icon: Users, 
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      href: '/admin/members'
    },
    { 
      title: 'Events', 
      value: formatNumber(stats.overview.totalEvents),
      subtitle: `${formatNumber(stats.overview.upcomingEvents)} Upcoming`,
      icon: Calendar, 
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-700',
      href: '/admin/events'
    },
    { 
      title: 'News Articles', 
      value: formatNumber(stats.overview.totalNews),
      subtitle: `${formatNumber(stats.overview.publishedNews)} Published`,
      icon: FileText, 
      color: 'from-amber-500 to-amber-600',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
      href: '/admin/news'
    },
    { 
      title: 'Event Registrations', 
      value: formatNumber(stats.overview.totalRegistrations),
      subtitle: `${formatNumber(stats.overview.confirmedRegistrations)} Confirmed`,
      icon: UserCheck, 
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700',
      href: '/admin/event-registrations'
    },
    { 
      title: 'Total Revenue', 
      value: formatCurrency(stats.overview.totalRevenue),
      subtitle: `${formatNumber(stats.overview.completedPayments)} Payments`,
      icon: DollarSign, 
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
      href: '/admin/payments'
    }
  ] : [];

  const recentActivity = stats?.recentActivity ? [
    { 
      title: 'New Members', 
      value: formatNumber(stats.recentActivity.recentMembers),
      icon: Users, 
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    { 
      title: 'New Events', 
      value: formatNumber(stats.recentActivity.recentEvents),
      icon: Calendar, 
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100'
    },
    { 
      title: 'New News', 
      value: formatNumber(stats.recentActivity.recentNews),
      icon: FileText, 
      color: 'text-amber-600',
      bgColor: 'bg-amber-100'
    },
    { 
      title: 'New Registrations', 
      value: formatNumber(stats.recentActivity.recentRegistrations),
      icon: UserCheck, 
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    { 
      title: 'New Payments', 
      value: formatNumber(stats.recentActivity.recentPayments),
      icon: CreditCard, 
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    }
  ] : [];

  const managementActions = [
    { 
      title: 'Manage Members', 
      description: 'View and manage all member registrations',
      icon: Users, 
      color: 'from-blue-500 to-blue-600',
      href: '/admin/members',
      badge: stats?.overview.pendingMembers || 0
    },
    { 
      title: 'Manage Events', 
      description: 'Create, edit, and manage events',
      icon: Calendar, 
      color: 'from-emerald-500 to-emerald-600',
      href: '/admin/events'
    },
    { 
      title: 'Manage News', 
      description: 'Create and publish news articles',
      icon: FileText, 
      color: 'from-amber-500 to-amber-600',
      href: '/admin/news'
    },
    { 
      title: 'Event Registrations', 
      description: 'View and manage event registrations',
      icon: UserCheck, 
      color: 'from-purple-500 to-purple-600',
      href: '/admin/event-registrations',
      badge: stats?.overview.pendingPayments || 0
    },
    { 
      title: 'Gallery Management', 
      description: 'Upload and manage photos and videos',
      icon: Image, 
      color: 'from-pink-500 to-pink-600',
      href: '/admin/gallery'
    },
    { 
      title: 'Partner Companies', 
      description: 'Manage homepage company marquee',
      icon: Building2,
      color: 'from-slate-500 to-slate-600',
      href: '/admin/companies'
    },
    { 
      title: 'Membership Plans', 
      description: 'Configure membership plans and pricing',
      icon: Settings, 
      color: 'from-indigo-500 to-indigo-600',
      href: '/admin/membership-plans'
    },
    { 
      title: 'Payment Management', 
      description: 'View payment history and transactions',
      icon: CreditCard, 
      color: 'from-green-500 to-green-600',
      href: '/admin/payments'
    }
  ];

  const quickLinks = [
    { 
      title: 'Member Registration', 
      description: 'Direct link to public registration form',
      url: `${window.location.origin}/member-registration`,
      icon: ExternalLink,
      color: 'from-purple-500 to-purple-600'
    },
    { 
      title: 'Portal Homepage', 
      description: 'View the public portal homepage',
      url: `${window.location.origin}`,
      icon: ExternalLink,
      color: 'from-cyan-500 to-cyan-600'
    }
  ];

  const handleExternalClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="w-full">
      {/* Page Header */}
      <div className="mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Dashboard</h2>
        <p className="text-sm text-gray-600">Monitor and manage your portal efficiently</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
            <p className="text-sm text-red-700 flex-1">{error}</p>
            <button
              onClick={loadDashboardStats}
              className="ml-4 text-sm text-red-600 hover:text-red-800 font-medium underline"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Main Stats Grid */}
      {statsLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse">
              <div className="h-8 bg-gray-200 rounded mb-3"></div>
              <div className="h-6 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {mainStats.map((stat, index) => (
            <div
              key={index}
              onClick={() => stat.href && navigate(stat.href)}
              className={`group bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-primary-300 transition-all duration-200 ${stat.href ? 'cursor-pointer' : ''}`}
            >
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className={`p-2 bg-gradient-to-br ${stat.color} rounded-lg shadow-sm`}>
                    <stat.icon className="h-4 w-4 text-white" />
                  </div>
                  {stat.href && (
                    <ArrowRight className="h-3.5 w-3.5 text-gray-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1.5 leading-tight">{stat.title}</p>
                  <p className="text-lg font-bold text-gray-900 mb-1">{stat.value}</p>
                  {stat.subtitle && (
                    <p className="text-xs text-gray-500 leading-tight">{stat.subtitle}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-8">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">Recent Activity</h3>
            <p className="text-xs text-gray-500">Last 7 days</p>
          </div>
          <Zap className="h-4 w-4 text-amber-500" />
        </div>
        {statsLoading ? (
          <div className="space-y-2.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-14 bg-gray-100 rounded-lg animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="space-y-2.5">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 hover:shadow-sm transition-all cursor-default">
                <div className="flex items-center gap-3">
                  <div className={`p-2 ${activity.bgColor} rounded-lg`}>
                    <activity.icon className={`h-4 w-4 ${activity.color}`} />
                  </div>
                  <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                </div>
                <p className="text-base font-bold text-gray-900">{activity.value}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Management Tools */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-8">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">Management Actions</h3>
            <p className="text-xs text-gray-500">Quick access to all admin functions</p>
          </div>
          <Settings className="h-5 w-5 text-gray-400" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {managementActions.map((action, index) => (
              <button
                key={index}
                onClick={() => navigate(action.href)}
                className="group relative p-4 bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-md transition-all duration-200 text-left"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`p-2.5 bg-gradient-to-br ${action.color} rounded-lg shadow-sm group-hover:scale-105 transition-transform`}>
                    <action.icon className="h-4 w-4 text-white" />
                  </div>
                  {action.badge > 0 && (
                    <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                      {action.badge}
                    </span>
                  )}
                </div>
                <h4 className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-primary-600 transition-colors">
                  {action.title}
                </h4>
                <p className="text-xs text-gray-600 leading-relaxed">{action.description}</p>
                <ArrowRight className="h-3.5 w-3.5 text-gray-400 mt-2 group-hover:text-primary-600 group-hover:translate-x-0.5 transition-all" />
                </button>
            ))}
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-lg shadow-sm border border-primary-200 p-5">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Quick Links</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {quickLinks.map((link, index) => (
              <div
                key={index}
                onClick={() => handleExternalClick(link.url)}
                className="group flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-sm transition-all cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 bg-gradient-to-br ${link.color} rounded-lg shadow-sm group-hover:scale-105 transition-transform`}>
                    <link.icon className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                      {link.title}
                    </h4>
                    <p className="text-xs text-gray-600">{link.description}</p>
              </div>
            </div>
                <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-primary-600 transition-colors" />
              </div>
            ))}
          </div>
        </div>
    </div>
  );
};

export default AdminDashboardPage;
